import React, { useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setPageName } from "../../../actions";
import { DashboardLayout } from "../../../components/Layouts";
import { DefaultPageContent } from "../../../components/PageContent";
import { VOC_BTN_FILTERS } from "../../../constants/vocConstants";
import { PageTitle } from "../../../components/PageContent/subs";
import VocQuickVocCecFilters from "../../../components/Filters/Voc/VocQuickVocCecFilters";
import { useQuery } from "@tanstack/react-query";
import "../../../assets/styles/pages/customerJourneyVoc.scss";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  getCustomerJourneyVocBuy,
  getCustomerJourneyVocFilters,
  getCustomerJourneyVocLastRefreshDate,
  getCustomerJourneyVocPerformanceByMarket,
  getCustomerJourneyVocTry,
  getCustomerJourneyVocUse,
} from "../../../api/customerJourneyVocApi";
import CustomerJourneyVocCategoryTile from "./components/CustomerJourneyVocCategoryTile";
import PerformanceByMarket from "../QuickVocSales/components/PerformanceByMarket";
import { getPerformanceByMarketMap } from "../../../api/quickVocAfterSales";
import { getQuickVocCecPerformanceByMarket } from "../../../api/quickVocCecApi";
import { DashboardBannerContext } from "../../../context";

const metricMapping: Record<string, string> = {
  Website: "website",
  "Quick VOC Sales": "voc_quick_sales",
  "CEC Information Request": "info",
  "CEC CCS": "ccs",
  "CCS Vital Signs": "ccs_vital_signs",
  "Quick VOC After Sales": "voc_after_sales",
  "CEC Complaints": "complaint",
};

const CustomerJourneyVoc = withRouter((props: RouteComponentProps) => {
  const dispatch = useDispatch();
  const { history } = props;

  const { data: lastDataRefreshData, refetch: refetchLastDataRefresh } = useQuery({
    queryKey: ["customerJourneyVocLastDataRefresh"],
    queryFn: getCustomerJourneyVocLastRefreshDate,
    enabled: false,
  });

  // state.page.pageName (what PageTitle actually renders) is global, not page-scoped - without
  // setting it here it keeps whatever the last VOC page that DID set it left behind (e.g. sticks
  // on "VOC (Voice Of Customer)" from Website.tsx after navigating here via the top tabs).
  useEffect(() => {
    dispatch(setPageName("Customer Journey VOC"));
  }, [dispatch]);

  const {
    data: customerJourneyVocTryData,
    isFetching: isFetchingCustomerJourneyVocTryData,
    refetch: refetchCustomerJourneyVocTryData,
  } = useQuery({
    queryKey: ["customerJourneyVocTry", history.location.search],
    queryFn: getCustomerJourneyVocTry,
    initialData: {},
    enabled: false,
  });

  const {
    data: customerJourneyVocBuyData,
    isFetching: isFetchingVocBuyData,
    refetch: refetchVocBuyData,
  } = useQuery({
    queryKey: ["customerJourneyVocBuy", history.location.search],
    queryFn: getCustomerJourneyVocBuy,
    initialData: {},
    enabled: false,
  });

  const {
    data: customerJourneyVocUseData,
    isFetching: isFetchingVocUseData,
    refetch: refetchVocUseData,
  } = useQuery({
    queryKey: ["customerJourneyVocUse", history.location.search],
    queryFn: getCustomerJourneyVocUse,
    initialData: {},
    enabled: false,
  });

  useEffect(() => {
    refetchLastDataRefresh();
    refetchCustomerJourneyVocTryData();
    refetchVocBuyData();
    refetchVocUseData();
  }, [history.location.search]);

  return (
    <DashboardLayout>
      <DefaultPageContent
        filter={<VocQuickVocCecFilters filterBtns={VOC_BTN_FILTERS} queryFn={getCustomerJourneyVocFilters} pageName="VOC 360" />}
        dataDocumentation="voc"
        lastDataRefresh={lastDataRefreshData?.date ?? ""}
        pageTitle={<PageTitle dataDocumentation="voc" />}
      >
        <div className="customer-journey-voc">
          <div className="grid-3">
            <CustomerJourneyVocCategoryTile
              title="Try"
              tiles={[
                {
                  title: "Website",
                  value: customerJourneyVocTryData?.website,
                  rating: customerJourneyVocTryData?.yoy,
                  volume: customerJourneyVocTryData?.volume,
                  previousPeriod: customerJourneyVocTryData?.website_previous_period,
                },
              ]}
              isLoading={isFetchingCustomerJourneyVocTryData}
            />
            <CustomerJourneyVocCategoryTile
              title="Buy"
              tiles={[
                {
                  title: "Quick VOC Sales",
                  value: customerJourneyVocBuyData?.voc_sales,
                  rating: customerJourneyVocBuyData?.voc_sales_yoy,
                  volume: customerJourneyVocBuyData?.voc_sales_volume,
                  previousPeriod: customerJourneyVocBuyData?.voc_sales_previous_period,
                },
                {
                  title: "CEC Information Request",
                  value: customerJourneyVocBuyData?.cec_info_req,
                  rating: customerJourneyVocBuyData?.cec_info_req_yoy,
                  volume: customerJourneyVocBuyData?.cec_info_req_volume,
                  previousPeriod: customerJourneyVocBuyData?.cec_info_req_previous_period,
                },
              ]}
              isLoading={isFetchingVocBuyData}
            />
            <CustomerJourneyVocCategoryTile
              title="Use"
              tiles={[
                {
                  title: "CEC CCS",
                  value: customerJourneyVocUseData?.ccs,
                  rating: customerJourneyVocUseData?.ccs_yoy,
                  volume: customerJourneyVocUseData?.ccs_volume,
                  previousPeriod: customerJourneyVocUseData?.ccs_previous_period,
                },
                {
                  title: "Quick VOC After Sales",
                  value: customerJourneyVocUseData?.voc_after_sales,
                  rating: customerJourneyVocUseData?.voc_after_sales_yoy,
                  volume: customerJourneyVocUseData?.voc_after_sales_volume,
                  previousPeriod: customerJourneyVocUseData?.voc_after_sales_previous_period,
                },
                {
                  title: "CCS Vital Signs",
                  value: customerJourneyVocUseData?.ccs_app_ratings,
                  rating: customerJourneyVocUseData?.ccs_app_ratings_yoy,
                  volume: customerJourneyVocUseData?.ccs_app_ratings_volume,
                  previousPeriod: customerJourneyVocUseData?.ccs_app_ratings_previous_period,
                },
                {
                  title: "CEC Complaints",
                  value: customerJourneyVocUseData?.complaints,
                  rating: customerJourneyVocUseData?.complaints_yoy,
                  volume: customerJourneyVocUseData?.complaints_volume,
                  previousPeriod: customerJourneyVocUseData?.complaints_previous_period,
                },
              ]}
              isLoading={isFetchingVocUseData}
            />
          </div>

          <PerformanceByMarket
            queryFn={getCustomerJourneyVocPerformanceByMarket}
            metrics={[
              "Website",
              "Quick VOC Sales",
              "CEC Information Request",
              "CEC CCS",
              "CCS Vital Signs",
              "Quick VOC After Sales",
              "CEC Complaints",
            ]}
            metricMapping={metricMapping}
          />
        </div>
      </DefaultPageContent>
    </DashboardLayout>
  );
});

export default CustomerJourneyVoc;
